import { Gamepad2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';
import { ChipSelect, Field, HexAvatar, LevelMark, Loading, ProgressBar } from '../components';
import { localTimezone } from '../format';
import { AVATAR_COLORS, ICON_SLUGS, renderIcon } from '../icons';
import { useApp } from '../store';
import { AVAILABILITY_OPTIONS, PLAY_STYLE_LABELS, VOICE_LABELS, type PlayerProfile, type PlayStyle, type VoicePreference } from '../types';
import { SettingsTabs } from './Settings';

const PLATFORM_OPTIONS = ['Crossplay', 'PC', 'PlayStation', 'Xbox', 'Nintendo Switch'];
const LANGUAGE_OPTIONS = ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Japanese'];

function ProfileForm({ profile }: { profile: PlayerProfile }) {
  const { setProfile, games, toast } = useApp();
  const [displayName, setDisplayName] = useState(profile.displayName);
  const [bio, setBio] = useState(profile.bio);
  const [pronouns, setPronouns] = useState(profile.pronouns ?? '');
  const [timezone, setTimezone] = useState(profile.timezone || localTimezone());
  const [playStyle, setPlayStyle] = useState<PlayStyle>(profile.playStyle);
  const [voice, setVoice] = useState<VoicePreference>(profile.voice);
  const [myGames, setMyGames] = useState<string[]>(profile.games);
  const [platforms, setPlatforms] = useState<string[]>(profile.platforms);
  const [languages, setLanguages] = useState<string[]>(profile.languages);
  const [availability, setAvailability] = useState<string[]>(profile.availability);
  const [avatarIcon, setAvatarIcon] = useState<string | null>(profile.avatarIcon);
  const [avatarColor, setAvatarColor] = useState<string | null>(profile.avatarColor);
  const [busy, setBusy] = useState(false);

  const toggle = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (value: string) =>
    setter((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value]);

  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await api.updateProfile({
        displayName,
        bio,
        pronouns,
        timezone,
        playStyle,
        voice,
        games: myGames,
        platforms,
        languages,
        availability,
        avatarIcon,
        avatarColor
      });
      setProfile(result.profile);
      toast('Profile saved.', 'success');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save your profile.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack-form" onSubmit={save}>
      <Field label="Avatar">
        <div className="avatar-picker">
          <HexAvatar label={profile.avatar} size="lg" icon={avatarIcon} accent={avatarColor} />
          <div className="avatar-picker-controls">
            <div className="icon-grid">
              <button type="button" className={`icon-choice ${!avatarIcon ? 'active' : ''}`} onClick={() => setAvatarIcon(null)} aria-label="Use initials" title="Initials">
                <span className="mono">{profile.avatar}</span>
              </button>
              {ICON_SLUGS.map((slug) => (
                <button type="button" key={slug} className={`icon-choice ${avatarIcon === slug ? 'active' : ''}`} onClick={() => setAvatarIcon(slug)} aria-label={slug} aria-pressed={avatarIcon === slug}>
                  {renderIcon(slug, 16)}
                </button>
              ))}
            </div>
            <div className="color-grid">
              <button type="button" className={`color-choice ${!avatarColor ? 'active' : ''}`} onClick={() => setAvatarColor(null)} aria-label="Default color" style={{ background: 'var(--card-2)' }} />
              {AVATAR_COLORS.map((color) => (
                <button type="button" key={color} className={`color-choice ${avatarColor === color ? 'active' : ''}`} onClick={() => setAvatarColor(color)} aria-label={color} aria-pressed={avatarColor === color} style={{ background: color }} />
              ))}
            </div>
          </div>
        </div>
      </Field>

      <div className="field-row">
        <Field label="Display name">
          <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} minLength={2} maxLength={40} required />
        </Field>
        <Field label="Pronouns (optional)">
          <input value={pronouns} onChange={(event) => setPronouns(event.target.value)} maxLength={30} placeholder="they/them" />
        </Field>
      </div>

      <Field label="Bio" hint="How you like to play, what you're grinding, what a good session looks like to you.">
        <textarea value={bio} onChange={(event) => setBio(event.target.value)} maxLength={400} rows={3} />
      </Field>

      <Field label="Timezone">
        <input value={timezone} onChange={(event) => setTimezone(event.target.value)} maxLength={60} placeholder="America/Denver" />
      </Field>

      <Field label="Your games">
        <ChipSelect
          options={games.map((game) => ({ value: game.slug, label: game.name }))}
          selected={myGames}
          onToggle={toggle(setMyGames)}
        />
      </Field>

      <Field label="Platforms">
        <ChipSelect options={PLATFORM_OPTIONS.map((value) => ({ value, label: value }))} selected={platforms} onToggle={toggle(setPlatforms)} />
      </Field>

      <Field label="When you usually play">
        <ChipSelect options={AVAILABILITY_OPTIONS.map((option) => ({ value: option.slug, label: option.label }))} selected={availability} onToggle={toggle(setAvailability)} />
      </Field>

      <div className="field-row">
        <Field label="Play style">
          <ChipSelect
            options={(Object.keys(PLAY_STYLE_LABELS) as PlayStyle[]).map((value) => ({ value, label: PLAY_STYLE_LABELS[value] }))}
            selected={[playStyle]}
            onToggle={(value) => setPlayStyle(value as PlayStyle)}
            single
          />
        </Field>
        <Field label="Voice chat">
          <ChipSelect
            options={(Object.keys(VOICE_LABELS) as VoicePreference[]).map((value) => ({ value, label: VOICE_LABELS[value] }))}
            selected={[voice]}
            onToggle={(value) => setVoice(value as VoicePreference)}
            single
          />
        </Field>
      </div>

      <Field label="Languages">
        <ChipSelect options={LANGUAGE_OPTIONS.map((value) => ({ value, label: value }))} selected={languages} onToggle={toggle(setLanguages)} />
      </Field>

      <button className="primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save profile'}</button>
    </form>
  );
}


export function ProfileView() {
  const { profile } = useApp();

  if (!profile) return <Loading label="Loading your profile…" />;

  const incomplete = !profile.bio || profile.games.length === 0 || profile.availability.length === 0;

  return (
    <div className="page narrow">
      <SettingsTabs active="profile" />

      <div className="profile-head">
        <HexAvatar label={profile.avatar} size="lg" icon={profile.avatarIcon} accent={profile.avatarColor} />
        <div>
          <h1>{profile.displayName}</h1>
          <p className="muted">@{profile.username}{profile.pronouns ? ` · ${profile.pronouns}` : ''}</p>
          <LevelMark level={profile.progression.level} title={profile.progression.title} />
        </div>
      </div>

      <div className="level-panel">
        <div className="level-panel-row">
          <span className="mono">{profile.progression.points} points</span>
          {profile.progression.nextLevelAt && <span className="mono muted">{profile.progression.nextLevelAt - profile.progression.points} to level {profile.progression.level + 1}</span>}
        </div>
        <ProgressBar value={profile.progression.progress} />
        <p className="muted small-text">
          Points come from playing: mutual post-game reviews confirm a session, first-time teammates earn a bonus,
          and hosting sessions that actually happen pays off. Creating groups alone earns nothing.
        </p>
      </div>

      {incomplete && (
        <div className="notice">
          <Gamepad2 size={15} />
          Finish your profile — games, availability, and a short bio help compatible squads find you.
        </div>
      )}

      <ProfileForm key={profile.id} profile={profile} />
    </div>
  );
}
